/**
 * Retry orchestration for RAILGUN wallet-instance creation.
 *
 * Extracted from the create-instance effect in `railgunContext.tsx` (the effect
 * guarded by `if (!hasWalletInstance)` near the "no wallet instance found,
 * creating it" log) so the backoff + re-fire decision is unit-testable in
 * isolation.
 *
 * Why this exists — the deadlock it fixes:
 *   On mobile-web (`railgunWorkerIsolationEnabled`) the scan engine lives in a
 *   nested worker, but `createRailgunWallet` runs the SDK *in the page*. The
 *   page-side `railgunEngineInitialized` flag flips true when the *worker*
 *   reports ready, which can race ahead of the in-page SDK engine. On a slow /
 *   cold machine the first `createRailgunWallet` therefore throws
 *   "RAILGUN Engine not yet initialized".
 *
 *   The create effect only re-runs when a React dependency identity changes.
 *   The original recovery relied on the `balanceUpdate` storm churning the
 *   `walletInstances` Map ~7×/s to re-fire the effect — but when the *first*
 *   instance creation fails there is no instance, hence no `getBalance`, hence
 *   no `balanceUpdate`, hence no churn, hence the effect never re-runs. The
 *   backoff timestamp was written but never read again → private balance stayed
 *   empty forever until a manual reload.
 *
 *   `scheduleWalletInstanceRetryRerun` breaks that deadlock by actively arming a
 *   timer that re-fires the effect after the backoff window, independent of any
 *   balance churn.
 */

export type WalletInstanceRetryState = { nextRetryAt: number; attempts: number }

/** Exponential backoff ceiling — matches the balance-sync retry cap. */
export const MAX_WALLET_INSTANCE_BACKOFF_MS = 30_000

/**
 * Backoff for the Nth (1-indexed) attempt: 1s, 2s, 4s, 8s … capped at 30s.
 */
export function computeWalletInstanceBackoffMs(attempts: number): number {
  return Math.min(MAX_WALLET_INSTANCE_BACKOFF_MS, 1000 * 2 ** (attempts - 1))
}

/** True while the account is still inside its post-failure backoff window. */
export function isWithinWalletInstanceBackoff(
  state: WalletInstanceRetryState | undefined,
  now: number
): boolean {
  return !!state && now < state.nextRetryAt
}

/**
 * Record a failed create attempt for `addr` and return the new retry state
 * (incremented attempt count + next-allowed timestamp). Mutates `retryMap`.
 */
export function registerWalletInstanceFailure(
  retryMap: Map<string, WalletInstanceRetryState>,
  addr: string,
  now: number
): WalletInstanceRetryState {
  const attempts = (retryMap.get(addr)?.attempts ?? 0) + 1
  const next: WalletInstanceRetryState = {
    attempts,
    nextRetryAt: now + computeWalletInstanceBackoffMs(attempts)
  }
  retryMap.set(addr, next)
  return next
}

/**
 * THE FIX. Arm a timer that re-fires the create effect once the backoff window
 * for `state` elapses, so recovery from a transient
 * "RAILGUN Engine not yet initialized" throw no longer depends on an external
 * `walletInstances` churn (which never arrives when the first create fails).
 *
 * `rerun` should bump a React state/ref that is in the effect's dependency
 * array (e.g. a `retryTick`), causing the effect to re-evaluate. `setTimer` is
 * injectable for tests; defaults to `setTimeout`. Returns the timer handle so
 * the caller can clear it on unmount.
 */
export function scheduleWalletInstanceRetryRerun(
  state: WalletInstanceRetryState,
  now: number,
  rerun: () => void,
  setTimer: (cb: () => void, ms: number) => ReturnType<typeof setTimeout> = setTimeout
): ReturnType<typeof setTimeout> {
  const delay = Math.max(0, state.nextRetryAt - now)
  return setTimer(rerun, delay)
}
